// Libraries
import React, {forwardRef} from 'react'
import classnames from 'classnames'

// Types
import {StandardFunctionProps, InfluxColors} from '../../Types'

// Styles
import './InfluxDBCloudLogo.scss'

export interface InfluxDBCloudLogoProps extends StandardFunctionProps {
  /** Coloration of the SVG image */
  fill?: InfluxColors | string
  /** Controls rendering of optional "cloud" suffix */
  cloud: boolean
}

export type InfluxDBCloudLogoRef = SVGSVGElement

export const InfluxDBCloudLogo = forwardRef<
  InfluxDBCloudLogoRef,
  InfluxDBCloudLogoProps
>(
  (
    {
      id,
      fill = InfluxColors.White,
      style,
      cloud,
      testID = 'logo--influxdb-cloud',
      className,
    },
    ref
  ) => {
    const logoClass = classnames('', {
      'cf-logo--influxdb-cloud': cloud,
      'cf-logo--influxdb': !cloud,
      [`${className}`]: className,
    })

    const logoStyle = {fill, ...style}

    let width = '503'
    const height = '156'
    let cloudVector = <></>

    if (cloud) {
      width = '850'
      cloudVector = (
        <path
          id="cloud"
          d="M598.9,56.7c-5.9-1.7-12.7-2.6-18-2.6c-13.9,0-18.9,5-21.9,21.8l-5.3,30.2c-0.2,1.2-0.3,2.3-0.3,3.4
	c0,5.9,3.7,8,11.2,8c7.5,0,15.9-1.7,24.7-5.3l1.4,7.3c-8.1,3.6-17.2,5.8-27.2,5.8c-11.2,0-18.9-5.3-18.9-15.6c0-1.4,0.2-3.1,0.5-4.7
	l5.5-30.7c3.7-20.6,11.7-28.1,31.2-28.1c5.9,0,14.2,1.4,18.9,3.1L598.9,56.7z M620.1,16.8l8.9-1.2L610,123.7h-8.7L620.1,16.8z
	 M631.1,104.1c0-2.2,0.2-4.5,0.6-6.9l4.1-23.1c3-16.7,11.7-27.9,34.2-27.9c16.9,0,25.3,8.7,25.3,21.4c0,1.9-0.3,3.7-0.6,5.8
	l-4.4,24.8c-3,16.8-12.3,27.1-34.2,27.1C638.1,125.3,631.1,116.2,631.1,104.1z M685.9,73.4c0.3-1.9,0.5-3.6,0.5-5.3
	c0-9.4-5.5-14-17.8-14c-14.4,0-21.7,6.9-24.1,20.1L640.3,98c-0.3,2-0.5,4.1-0.5,5.8c0,8.9,4.8,13.7,17.7,13.7
	c14.1,0,21.7-6.5,24.1-19.5L685.9,73.4z M713.6,109.5c-0.2,0.9-0.3,2-0.3,2.8c0,3.1,2,4.8,6.4,4.8c8,0,22.8-7,32.5-13.1l9.8-55.6
	l9.1-1.2l-13.6,76.5H750l1.2-11.1c-8.7,6.1-23.4,12.6-33.1,12.6c-9.4,0-13.4-4.8-13.4-12.5c0-1.6,0.2-3.1,0.5-4.8l10.5-59.5l9.1-1.2
	L713.6,109.5z M824.5,112.7c-8.7,7.5-20.2,12.6-31.1,12.6c-9.7,0-15-3.4-15-12.8c0-1.7,0.2-3.6,0.5-5.6l5.8-32.6
	c3.6-20.6,13-28.1,32.3-28.1c5,0,13.7,1.1,18.4,2.5l5.6-32l8.9-1.2l-19.1,108.2h-8L824.5,112.7z M834.1,56.5
	c-6.1-1.6-12.2-2.5-18.3-2.5c-13.9,0-19.7,5-22.7,21.8l-5.5,31c-0.3,1.9-0.5,3.3-0.5,4.5c0,4.8,2.3,6.1,7,6.1
	c9.7,0,21.9-6.2,31.7-15L834.1,56.5z"
        />
      )
    }

    return (
      <svg
        version="1.1"
        id={id}
        ref={ref}
        data-testid={testID}
        style={logoStyle}
        x="0px"
        y="0px"
        className={logoClass}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
      >
        <path
          id="influxdb"
          d="M13.1,49.5l22.7-3.1L22,123.7H0L13.1,49.5z M19.1,16l22.7-3.1l-4.2,23.4L15,39.4L19.1,16z M90.2,72.1
	c0.2-0.9,0.3-1.7,0.3-2.3c0-1.6-0.8-2.2-3-2.2c-4.1,0-13.7,4.1-19.7,7.2l-8.6,48.9h-22l13-74l19.4-3.3l-0.2,8.4
	c11.9-5.6,21.2-8.6,30-8.6c10.2,0,13.9,5.8,13.9,15.4c0,2.7-0.2,4.8-0.8,8.7l-9.4,53.3h-22L90.2,72.1z M111.1,142.9
	c7.7-7.8,8.7-11.8,10-19.2l10.3-58h-10.9l3.1-17.9h10.9l1.7-10c3.1-17.5,9.7-24.2,29.2-24.2c6.2,0,17,1.1,26.6,3.7l22.8-3.7
	l-19.4,110.1h-22l15.6-89.3c-5.9-1.6-16.6-2.8-23.1-2.8c-5,0-6.6,1.2-7.3,5.1l-2,11.1h19.7l-4.7,17.9h-18.1l-10.6,60.5
	c-1.9,10.6-5.6,18.1-17.5,29.8L111.1,142.9z M236.4,99.3c-0.2,0.8-0.3,1.7-0.3,2.3c0,1.6,0.8,2.3,3,2.3c4.1,0,14.1-4.4,19.7-7.2
	l8.3-47.2l22.7-3.1l-13.6,77.3h-18.7l-0.2-7c-11.9,5.6-21.2,8.6-30,8.6c-10,0-13.9-5.9-13.9-15.4c0-3.1,0.3-5.6,0.8-8.7l9.1-51.6
	l22.5-3.1L236.4,99.3z M322.9,104.7l-15.8,19h-23.3L314,85.5l-16.4-36l21.1-3.1l9.8,25.3L345,47.8h23.4l-30.6,40.7l16.1,35.2h-22
	L322.9,104.7z M408.5,112.7c-8.7,7.5-20.2,12.6-31.1,12.6c-9.7,0-15-3.4-15-12.8c0-1.7,0.2-3.6,0.5-5.6l5.8-32.6
	c3.6-20.6,13-28.1,32.3-28.1c5,0,13.7,1.1,18.4,2.5l5.6-32l8.9-1.2l-19.1,108.2h-8L408.5,112.7z M418.1,56.5
	c-6.1-1.6-12.2-2.5-18.3-2.5c-13.9,0-19.7,5-22.7,21.8l-5.5,31c-0.3,1.9-0.5,3.3-0.5,4.5c0,4.8,2.3,6.1,7,6.1
	c9.7,0,21.9-6.2,31.7-15L418.1,56.5z M454.3,16.8l8.9-1.2l-7.5,43c8.6-7.3,19.8-12.3,30.6-12.3c9.7,0,15.2,3.4,15.2,12.8
	c0,1.7-0.3,3.6-0.6,5.6L495,97.2c-3.4,19.5-11.9,28.1-32.3,28.1c-7,0-17.5-1.2-26.9-4.4L454.3,16.8z M445.5,115.6
	c4.8,1.1,12.2,1.9,18.4,1.9c14.2,0,19.7-5.1,22.7-21.8l5.5-31c0.3-1.9,0.5-3.3,0.5-4.7c0-4.8-2.3-5.9-7-5.9c-9.7,0-21.9,6.2-31.7,15
	L445.5,115.6z"
        />
        {cloudVector}
      </svg>
    )
  }
)

InfluxDBCloudLogo.displayName = 'InfluxDBCloudLogo'
